import { isKeyHotkey } from 'is-hotkey'
import React from 'react'
import { Range, Transforms } from 'slate'
import { Editable, RenderElementProps, RenderLeafProps, useSelected } from 'slate-react'
import { EditableProps } from 'slate-react/dist/components/editable'
import theme from 'src/theme'
import styled from 'styled-components'

import { TagSpan } from '../components/Tag/styles'
import { default as Tag } from '../components/Tag/Tag'
import { CustomBaseElement, CustomBaseText, CustomDescendant, SlateEditor } from '../types/slateTypes'

const StyledUl = styled.ul`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  column-gap: 8px;
  margin-block-start: 0;
  margin-block-end: 0;
`
const BoldText = styled.span`
  font-weight: ${theme.fontWeights.semiBold};
`

export const renderText = ({ attributes, children, leaf }: RenderLeafProps) => (
  <span
    // The following is a workaround for a Chromium bug where,
    // if you have an inline at the end of a block,
    // clicking the end of a block puts the cursor inside the inline
    // instead of inside the final {text: ''} node
    // https://github.com/ianstormtaylor/slate/issues/4704#issuecomment-1006696364
    style={leaf.text === '' ? { paddingLeft: '0.1px' } : undefined}
    {...attributes}
  >
    {(leaf as CustomBaseText).bold ? <BoldText>{children}</BoldText> : children}
  </span>
)

interface RenderElementPropsExtended extends RenderElementProps {
  listItemLineHeight?: string
}

export const RenderElement = ({ attributes, children, element, listItemLineHeight }: RenderElementPropsExtended) => {
  const selected = useSelected()

  switch ((element as CustomBaseElement).type) {
    case 'badge':
      return (element as CustomBaseElement).component === 'Tag' ? (
        <TagSpan {...attributes} contentEditable={false} data-playwright-selected={selected}>
          <InlineChromiumBugfix />
          {children}
          <InlineChromiumBugfix />
        </TagSpan>
      ) : (
        <span
          {...attributes}
          contentEditable={false}
          data-slate-void
          data-playwright-selected={selected}
          style={{
            background: theme.colors.gray1,
            border: `1px solid ${theme.colors.gray3}`,
            padding: '5px 4px',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none',
          }}
        >
          <InlineChromiumBugfix />
          {children}
          <InlineChromiumBugfix />
        </span>
      )

    case 'list-item':
      return (
        <ul style={{ paddingInlineStart: '1em', margin: '15px 0' }}>
          <li style={{ lineHeight: listItemLineHeight ?? '2em' }}>{children}</li>
        </ul>
      )
    default:
      return <p {...attributes}>{children}</p>
  }
}

// Put this at the start and end of an inline component to work around this Chromium bug:
// https://bugs.chromium.org/p/chromium/issues/detail?id=1249405
const InlineChromiumBugfix = () => (
  <span contentEditable={false} style={{ fontSize: 0 }}>
    {String.fromCodePoint(160) /* Non-breaking space */}
  </span>
)

// Force empty paragraphs to inherit line-height (render empty row)
const SlateToHtmlWrapper = styled.span`
  p:after {
    content: '';
    display: inline-block;
    width: 0px;
  }
`

interface CustomEditableProps extends EditableProps {
  editor: SlateEditor
  listItemLineHeight?: string
}
export const CustomEditable = (props: CustomEditableProps) => {
  const { editor, listItemLineHeight, ...otherProps } = props
  const fixForOnKeyDown = (event: React.KeyboardEvent): React.KeyboardEventHandler<HTMLDivElement> | undefined => {
    // Default left/right behavior is unit:'character'.
    // This fails to distinguish between two cursor positions, such as
    // <inline>foo<cursor/></inline> vs <inline>foo</inline><cursor/>.
    // Here we modify the behavior to unit:'offset'.
    // This lets the user step into and out of the inline without stepping over characters.
    // You may wish to customize this further to only use unit:'offset' in specific cases.
    if (editor.selection && Range.isCollapsed(editor.selection)) {
      const { nativeEvent } = event
      if (isKeyHotkey('left', nativeEvent)) {
        event.preventDefault()
        Transforms.move(editor, { unit: 'offset', reverse: true })
        return
      }
      if (isKeyHotkey('right', nativeEvent)) {
        event.preventDefault()
        Transforms.move(editor, { unit: 'offset' })
        return
      }
    }
  }

  return (
    <Editable
      renderElement={(p) => <RenderElement {...p} listItemLineHeight={listItemLineHeight} />}
      renderLeaf={renderText}
      onKeyDown={fixForOnKeyDown}
      {...otherProps}
    />
  )
}

interface SlateToHtmlProps {
  slateList?: CustomDescendant[]
  parentKey?: string
  firstParagraphInList?: boolean
}
/**
 * Converts SlateJS value list to HTML (CSS)
 * Result looks like in editor but without badge styles
 */
export const SlateToHtml = ({ slateList, parentKey, firstParagraphInList }: SlateToHtmlProps) => {
  return (
    <SlateToHtmlWrapper>
      {slateList?.map((node, i) => {
        const key = parentKey ? parentKey + '-' + i : i.toString()
        const textNode = node as CustomBaseText
        if (textNode.text) {
          return (
            <React.Fragment key={key}>
              {textNode.bold ? <BoldText>{textNode.text}</BoldText> : textNode.text}
            </React.Fragment>
          )
        } else {
          const element = node as CustomBaseElement
          switch (element.type) {
            case 'list-item':
              return (
                <StyledUl key={key}>
                  <li>
                    <SlateToHtml slateList={element.children} parentKey={key} firstParagraphInList />
                  </li>
                  {element.tag && <Tag text={element.tag} />}
                </StyledUl>
              )
            case 'paragraph':
              if (!firstParagraphInList || i !== 0) {
                return (
                  <p key={key}>
                    <SlateToHtml slateList={element.children} parentKey={key} />
                  </p>
                )
              }
            // we want fall through if the above condition is false
            // eslint-disable-next-line no-fallthrough
            default:
              return <SlateToHtml key={key} slateList={element.children} parentKey={key} />
          }
        }
      })}
    </SlateToHtmlWrapper>
  )
}
